/* eslint-disable arrow-body-style */
import * as React from "react";
import {Component } from "react";

import {observer} from "mobx-react";

import {Button, Grid, Item, Loader, Popup} from "semantic-ui-react";

import "./project-list.scss";

import {AppState} from "../../components/app/app-mobx";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";

import {ProjectItem} from "./project-item";
import {NavigationConfiguration} from "../../config/navigation-configuration";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";
import {Project} from "../../models/project";
import {Icon} from "../../core/components/icon/icon";
import {ProjectSearch} from "./project-search";
import {ProjectFilter} from "./project-filter";
import {ProjectSorter} from "./project-sorter";
import {ProjectStatusToggle} from "./project-status-toggle";


export interface ProjectListProps {
    auftragsverwaltung: Auftragsverwaltung;
    appStore: AppState;
    mapViewerConfig: MapViewerConfiguration;
    projects?: Project[] | null;
    projectType?: "GK" | "RISK";
    listOnly?: boolean;
}

@observer
export class ProjectList extends Component<ProjectListProps, any> {

    constructor(props: ProjectListProps) {
        super(props);
        this.state = {
            highlightedItemId: null
        };
    }

    componentDidMount(): void {
        const { auftragsverwaltung, appStore, listOnly} = this.props;

        if (listOnly) {
            // do not set title and breadcrumbs when using list on other pages
            return;
        }

        auftragsverwaltung.setShowMap(false);

        const {topic} = appStore;

        const newRoute = topic.routes[0];
        const breadcrumbs = new NavigationConfiguration();
        breadcrumbs.items = [
            {href: newRoute, title: topic.label}
        ];
        appStore.setBreadcrumbs(breadcrumbs);

        appStore.setPageTitle(topic.label);
        appStore.setTopContent(null);
        appStore.setBottomContent(null);
    }

    render(): React.ReactElement | null {
        const {
            auftragsverwaltung,
            listOnly,
            projects,
            projectType
        } = this.props;
        const isLoading = auftragsverwaltung.ProjectStore?.isDataLoading;

        let visibleItems: Project[] | null | undefined = projects;
        if (!projects) {
            if (projectType === "GK") {
                visibleItems = auftragsverwaltung.ProjectStore?.visibleItemsGk;
            }
            else if (projectType === "RISK") {
                visibleItems = auftragsverwaltung.ProjectStore?.visibleItemsRisk;
            }
        }

        if (!visibleItems) {
            return null;
        }

        const newProjectButton = (
            <Popup content="Neues Projekt"
                trigger={
                    <Button className={"new-project"} circular
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            // Wenn blur direkt via e.target aufgerufen wird, reklamiert Webstorm:
                            // TS2339: Property 'blur' does not exist on type 'EventTarget'.
                            const target = e.target as HTMLElement;
                            target.blur();

                            this.handleNewProject();
                        }}>
                        <Icon name={"navigate_plus"} />
                    </Button>
                } />
        );

        let content: React.ReactElement | undefined;
        if (visibleItems.length === 0) {
            content = (<div>
                <Item size="mini">
                    <Item.Content className="no-results">
                        Keine Projekte gefunden.
                    </Item.Content>
                </Item>
            </div>);
        }
        else {
            content = (
                <Item.Group divided link>
                    {visibleItems.map((project: Project) => (
                        <ProjectItem
                            key={project.id}
                            project={project}
                            auftragsverwaltung={auftragsverwaltung}
                            handleShowProject={(p: Project) => {
                                this.showProject(p);
                            }}
                        />
                    ))}
                </Item.Group>
            );
        }

        return (<>
            {!listOnly &&
                <Grid>
                    <Grid.Row className="first-row">
                        <Grid.Column width={5}>
                            <ProjectSearch auftragsverwaltung={auftragsverwaltung} />
                        </Grid.Column>
                        <Grid.Column width={8}>
                            <ProjectFilter
                                auftragsverwaltung={auftragsverwaltung}
                                processType={projectType === "GK" ? "gefahrenkarte" : "risikoübersicht"}
                            />
                        </Grid.Column>
                        <Grid.Column width={3}>
                            <ProjectSorter auftragsverwaltung={auftragsverwaltung} />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="second-row">
                        <Grid.Column width={2}>
                            {newProjectButton}
                        </Grid.Column>
                        <Grid.Column width={1}>
                            {isLoading && <Loader active inline />}
                        </Grid.Column>
                        <Grid.Column floated="right" width={8}>
                            <ProjectStatusToggle auftragsverwaltung={auftragsverwaltung} />
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            }
            {content}
        </>);
    }

    private showProject(project: Project): void {
        const {
            auftragsverwaltung,
            appStore,
            mapViewerConfig
        } = this.props;

        if (!project?.id) {
            return;
        }
        // request full details --> will select project when loaded
        auftragsverwaltung.ProjectStore?.loadItem(project.id).then((p: Project) => {
            if (p?.id) {
                // change route if selected project from auftragnehmer
                if(appStore.topic?.id === "contractors") {
                    // selected project on contractors list --> change topic
                    const topicId = p.typeId === 4 ? "projectsR" : "projectsGk";
                    const topic = mapViewerConfig.getTopic(topicId);
                    if (!topic) {
                        throw new Error("Topic not found loading project.");
                    }
                    appStore.setRoutePath(topic.routes[0]);
                    appStore.setTopic(topic);

                    const projectId = p.id.toString();
                    setTimeout(() => {
                        // Do this after setting topic. setTopic will unselect project otherwise
                        auftragsverwaltung.setShowMap(false);
                        appStore.setObjectId(projectId);
                        auftragsverwaltung.ProjectStore.setSelectedItem(p);
                    }, 200);
                }
                else {
                    // selected project from project list
                    auftragsverwaltung.setShowMap(false);
                    appStore.setObjectId(p.id.toString());
                }
            }
        });
    }

    private handleNewProject(): void {
        const {auftragsverwaltung, appStore} = this.props;

        const user = appStore.esriCredential?.userId || "";
        auftragsverwaltung.ProjectStore?.newItem(user).then((newProject) => {
            if (!newProject) {
                throw new Error("Error creating new project.");
            }
            if (newProject?.id) {
                // do not show map when editing
                auftragsverwaltung.setShowMap(false);
                appStore.setObjectId(newProject.id.toString());
                auftragsverwaltung.setEditing(true, "project");
            }
        });
    }
}
